import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/auth.store';
import {
  DEFAULT_INACTIVITY_MINUTES,
  getInactivityMinutes,
  SESSION_TIMEOUT_EVENT,
  type InactivityMinutes,
} from '../lib/sessionTimeout';

const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'] as const;

/** Déconnexion automatique après inactivité (tous comptes connectés). */
export function useInactivityLogout(): void {
  const navigate = useNavigate();
  const { user, isAuthenticated, logout } = useAuthStore();
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [minutes, setMinutes] = useState<InactivityMinutes>(DEFAULT_INACTIVITY_MINUTES);

  const active = isAuthenticated && Boolean(user?.id);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const scheduleLogout = useCallback(() => {
    clearTimer();
    if (!active || minutes <= 0) return;

    timerRef.current = setTimeout(() => {
      logout();
      navigate('/login?reason=inactivity', { replace: true });
    }, minutes * 60 * 1000);
  }, [active, minutes, clearTimer, logout, navigate]);

  useEffect(() => {
    if (!active || !user?.id) return;
    setMinutes(getInactivityMinutes(user.id));
  }, [active, user?.id]);

  useEffect(() => {
    if (!active || !user?.id) return;
    const userId = user.id;

    function onTimeoutChange(e: Event) {
      const detail = (e as CustomEvent<{ userId?: string }>).detail;
      if (detail?.userId && detail.userId !== userId) return;
      setMinutes(getInactivityMinutes(userId));
    }

    window.addEventListener(SESSION_TIMEOUT_EVENT, onTimeoutChange);
    return () => window.removeEventListener(SESSION_TIMEOUT_EVENT, onTimeoutChange);
  }, [active, user?.id]);

  useEffect(() => {
    if (!active) {
      clearTimer();
      return;
    }

    scheduleLogout();

    const onActivity = () => scheduleLogout();
    for (const evt of ACTIVITY_EVENTS) {
      window.addEventListener(evt, onActivity, { passive: true });
    }

    return () => {
      clearTimer();
      for (const evt of ACTIVITY_EVENTS) {
        window.removeEventListener(evt, onActivity);
      }
    };
  }, [active, scheduleLogout, clearTimer]);
}

/** @deprecated Utiliser useInactivityLogout */
export const useAdminInactivityLogout = useInactivityLogout;
